import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Onboarding } from '@ofself/shell';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { EVENTS } from '../../shared';
import { joinNext, withNext } from '../../utils/joinFlow';
import JoinFrame, { joinStages } from './JoinFrame';

// JOIN, STEP 4 — a photo, optional. Saved onto the profile the account was
// made with the moment it is picked (SettingsPage's rule: a photo that waits
// for a second button is a photo people lose), so Next only moves on.
function JoinPhoto() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = joinNext(searchParams);
  const input = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const profileId = user?.default_profile_node_id;
    if (!profileId) { setError('Your profile is not ready yet. Try again in a moment.'); return; }
    setBusy(true);
    setError('');
    try {
      const res = await api.uploadFileAsNode(file);
      const fileId = (res.node || res).id;
      await api.updateNode(profileId, { value_json: { avatar_file_id: fileId } });
      setPreview(URL.createObjectURL(file));
      window.dispatchEvent(new CustomEvent(EVENTS.PROFILE_AVATAR_CHANGED));
    } catch (err) {
      setError(err.message || 'That photo could not be uploaded.');
    } finally {
      setBusy(false);
    }
  };

  const onward = () => navigate(withNext('/join/codes', next), { replace: true });

  return (
    <JoinFrame stages={joinStages('photo', next)}>
      <Onboarding.Step
        title="A photo"
        subtitle="Optional. It is how the people and apps you choose will see you."
        centered
      >
        <button
          type="button"
          className="join-photo"
          onClick={() => input.current?.click()}
          disabled={busy}
          aria-label={preview ? 'Change photo' : 'Choose a photo'}
        >
          {preview
            ? <img src={preview} alt="" className="join-photo__img" />
            : <span className="join-photo__empty">{busy ? 'Uploading…' : 'Choose a photo'}</span>}
        </button>
        <input ref={input} type="file" accept="image/*" hidden onChange={onPick} />
        {error && <p className="account-error" role="alert">{error}</p>}
        <Onboarding.Next onClick={onward} disabled={busy}>{preview ? 'Continue' : 'Skip for now'}</Onboarding.Next>
      </Onboarding.Step>
    </JoinFrame>
  );
}

export default JoinPhoto;
